/**
 * @file     ListingHeroCarousel.tsx
 * @purpose  Phone hero: a swipeable 4:3 photo carousel with the "n / total"
 *           count and the media pills, then a 4-up thumb strip whose last
 *           cell opens the photo grid. Every photo opens the lightbox at its
 *           index. Photo 1 is the LCP image (priority, fetchPriority high,
 *           the shared HERO_SIZES); only the first 3 slides render an image
 *           on load, later ones mount as the visitor swipes toward them. The
 *           track is native scroll-snap (useSnapTrack): the photo follows the
 *           finger and glides one slide per swipe.
 */
'use client';

import { useState, type ReactNode } from 'react';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import { alpha } from '@mui/material/styles';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { mlsCardImageLoader, mlsHeroImageLoader } from '@/services/mls/mlsImageLoader';
import { colorPalettesByMode, fontWeightTokens, glassMaterial, layoutTokens } from '@/design-system/tokens';
import { propertyDetailContent as C } from '../../pageContent';
import type { DetailMedia } from '../../UnifiedDetail';
import { HERO_QUALITY, HERO_SIZES } from '../galleryModel';
import { GalleryIcon } from '../galleryIcons';
import { useSnapTrack } from '../useSnapTrack';

const R = layoutTokens.radius;
const EAGER_SLIDES = 3;
const ROLE_CAROUSEL = { key: 'pages.propertyDetail.gallery.roleCarousel', defaultValue: 'carousel' };

interface Props {
  media: DetailMedia[];
  titleAlt: string;
  failed: ReadonlySet<string>;
  onPhotoFailed: (key: string) => void;
  onOpenPhoto: (index: number) => void;
  onShowAll: () => void;
  /** Media pills (at most two on a phone), bottom left of the photo. */
  pills?: ReactNode;
  statusChip?: ReactNode;
  /** Quick views: a rounded photo and a flush thumb strip inside the host's column. */
  compact?: boolean;
}

export function ListingHeroCarousel({ media, titleAlt, failed, onPhotoFailed, onOpenPhoto, onShowAll, pills, statusChip, compact = false }: Props) {
  const { theme } = useAppTheme();
  const { t } = useTranslation();
  const tx = (ref: { key: string; defaultValue: string }, vars?: Record<string, unknown>) =>
    t(ref.key, { defaultValue: ref.defaultValue, ...vars });
  const total = media.length;
  const { ref: trackRef, index: current } = useSnapTrack({ count: total });
  // Highest slide index allowed to mount an <img>. Grows with swiping; never shrinks.
  const [reach, setReach] = useState(EAGER_SLIDES - 1);
  if (current + 1 > reach) setReach(current + 1);

  const placeholder = theme.semanticColors.surfaceElevated;
  const thumbs = media.slice(1, 5);
  const more = total - 4;

  return (
    <Box>
      <Box sx={{ position: 'relative', aspectRatio: '4 / 3', bgcolor: placeholder, ...(compact ? { borderRadius: `${R.md}px`, overflow: 'hidden' } : null) }}>
        <Box
          ref={trackRef}
          role="region"
          aria-roledescription={tx(ROLE_CAROUSEL)}
          aria-label={tx(C.gallery.a11yLabel)}
          sx={{
            display: 'flex',
            height: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollSnapType: 'x mandatory',
            overscrollBehaviorX: 'contain',
            touchAction: 'pan-x pan-y pinch-zoom',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {media.map((m, i) => (
            <ButtonBase
              key={m.key}
              onClick={() => onOpenPhoto(i)}
              disableRipple
              aria-label={tx(C.gallery.openPhoto, { current: i + 1, total })}
              sx={{ position: 'relative', flex: '0 0 100%', height: '100%', scrollSnapAlign: 'center', scrollSnapStop: 'always', display: 'block' }}
            >
              {i <= reach && !failed.has(m.key) ? (
                <Image
                  src={m.url}
                  loader={mlsHeroImageLoader}
                  alt={m.caption ? tx(C.gallery.photoAltCaption, { caption: m.caption, title: titleAlt }) : tx(C.gallery.photoAltAt, { current: i + 1, total, title: titleAlt })}
                  fill
                  priority={i === 0}
                  fetchPriority={i === 0 ? 'high' : undefined}
                  loading={i === 0 ? undefined : 'lazy'}
                  sizes={i === 0 ? HERO_SIZES : '100vw'}
                  quality={i === 0 ? HERO_QUALITY : 75}
                  style={{ objectFit: 'cover' }}
                  draggable={false}
                  onError={() => onPhotoFailed(m.key)}
                />
              ) : failed.has(m.key) ? (
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.semanticColors.textMuted }}>
                  <GalleryIcon name="image" size={22} />
                </Box>
              ) : null}
            </ButtonBase>
          ))}
        </Box>

        {statusChip ? <Box sx={{ position: 'absolute', top: 12, left: 12, zIndex: 1 }}>{statusChip}</Box> : null}
        {pills ? <Box sx={{ position: 'absolute', left: 12, bottom: 12, display: 'flex', gap: 1.5, zIndex: 1 }}>{pills}</Box> : null}
        {total > 1 ? (
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              right: 12,
              bottom: 12,
              zIndex: 1,
              height: 32,
              px: 3,
              display: 'inline-flex',
              alignItems: 'center',
              borderRadius: `${R.pill}px`,
              ...glassMaterial('ultraThin'),
              color: theme.semanticColors.textPrimary,
              fontSize: 12.5,
              fontWeight: fontWeightTokens.semibold,
              fontVariantNumeric: 'tabular-nums',
              pointerEvents: 'none',
            }}
          >
            {tx(C.gallery.counterShort, { current: current + 1, total })}
          </Box>
        ) : null}
      </Box>

      {thumbs.length > 0 ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: compact ? '8px' : '6px', pt: compact ? '8px' : '6px', px: compact ? 0 : 5 }}>
          {thumbs.map((m, j) => {
            const i = j + 1;
            const isMore = j === 3 && more > 1;
            return (
              <ButtonBase
                key={m.key}
                onClick={isMore ? onShowAll : () => onOpenPhoto(i)}
                aria-label={isMore ? tx(C.gallery.showAll, { count: total }) : tx(C.gallery.openPhoto, { current: i + 1, total })}
                sx={{
                  position: 'relative',
                  aspectRatio: '4 / 3',
                  borderRadius: `${R.sm - 4}px`,
                  overflow: 'hidden',
                  bgcolor: placeholder,
                  '&:focus-visible': { outline: `2px solid ${theme.semanticColors.interactiveFocusRing}`, outlineOffset: 2 },
                }}
              >
                {failed.has(m.key) ? null : (
                  <Image src={m.url} loader={mlsCardImageLoader} alt="" fill loading="lazy" sizes="25vw" quality={60} style={{ objectFit: 'cover' }} onError={() => onPhotoFailed(m.key)} />
                )}
                {isMore ? (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(colorPalettesByMode.dark.neutral[900], 0.55),
                      color: colorPalettesByMode.dark.semantic.textPrimary,
                      fontSize: 14,
                      fontWeight: fontWeightTokens.semibold,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {tx(C.gallery.moreCount, { count: more })}
                  </Box>
                ) : null}
              </ButtonBase>
            );
          })}
        </Box>
      ) : null}
    </Box>
  );
}
